import React, { useMemo } from "react"
import { AbsoluteFill, getRemotionEnvironment, useVideoConfig } from "remotion"
import { SourceAudio, VideoTrack } from "./tracks/VideoTrack"
import { ReframeLayer } from "./tracks/ReframeLayer"
import { SceneReframeLayer } from "./tracks/SceneReframeLayer"
import { CaptionTrack } from "./tracks/CaptionTrack"
import { HookTrack } from "./tracks/HookTrack"
import { BRollTrack } from "./tracks/BRollTrack"
import { MusicTrack } from "./tracks/MusicTrack"
import { VoiceoverTrack } from "./tracks/VoiceoverTrack"
import { GifTrack } from "./tracks/GifTrack"
import { EmojiTrack } from "./tracks/EmojiTrack"
import { WatermarkOverlay } from "./tracks/WatermarkOverlay"
import { EffectsLayer } from "./tracks/EffectsLayer"
import type { CompositionProps } from "../schemas"
import { speechRangesFromScript } from "../utils/duck"
import { captionBands } from "../utils/placement"
import { resolveCaptionStyleConfig } from "@/lib/caption-display"

export const ClipComposition: React.FC<CompositionProps> = (props) => {
  const { tracks, renderPref, sourceVideoUrl, applyWatermark = false, sceneMetadata } = props

  const captionTrack = tracks.find(t => t.trackType === "CaptionTrack" && t.visible)
  const hookTrack = tracks.find(t => t.trackType === "HookTrack" && t.visible)
  const brollTrack = tracks.find(t => t.trackType === "BRollTrack" && t.visible)
  const musicTrack = tracks.find(t => t.trackType === "MusicTrack" && t.visible)
  const voiceoverTrack = tracks.find(t => t.trackType === "VoiceoverTrack" && t.visible)
  const gifTrack = tracks.find(t => t.trackType === "GifTrack" && t.visible)
  const emojiTrack = tracks.find(t => t.trackType === "EmojiTrack" && t.visible)
  const reframeTrack = tracks.find(t => t.trackType === "ReframeTrack" && t.visible)
  const effectsTrack = tracks.find(t => t.trackType === "EffectsTrack" && t.visible)

  const duckSegments = voiceoverTrack
    ? voiceoverTrack.sections
        .flatMap(section => section.segments)
        .map((segment: any) => {
          const settings = segment?.content?.audioSettings ?? {}
          return {
            startMs: segment?.timeline?.in ?? 0,
            endMs: segment?.timeline?.out ?? 0,
            duckOriginalAudio: settings.duckOriginalAudio ?? true,
            duckLevel: settings.duckLevel ?? 30,
          }
        })
    : []

  const gifDuckSegments = gifTrack
    ? gifTrack.sections
        .flatMap(section => section.segments)
        .map((segment: any) => {
          const settings = segment?.content?.audioSettings ?? {}
          return {
            startMs: segment?.timeline?.in ?? 0,
            endMs: segment?.timeline?.out ?? 0,
            duckOriginalAudio: settings.duckOriginalAudio ?? true,
            duckLevel: settings.duckLevel ?? 0,
          }
        })
    : []

  const combinedDuckSegments = [...duckSegments, ...gifDuckSegments]
  const hasScenes = !!sceneMetadata && sceneMetadata.chunks?.length > 0
  const { fps, durationInFrames } = useVideoConfig()
  const hookAvoid = useMemo(
    () => (renderPref.enableCaption && captionTrack
      ? captionBands(resolveCaptionStyleConfig(captionTrack.styleConfig), sceneMetadata, fps, durationInFrames)
      : []),
    [renderPref.enableCaption, captionTrack, sceneMetadata, fps, durationInFrames],
  )
  const speechRanges = useMemo(
    () => (musicTrack ? speechRangesFromScript(tracks, sceneMetadata?.speech_segments) : []),
    [musicTrack, tracks, sceneMetadata?.speech_segments],
  )

  return (
    <AbsoluteFill>
      {/* Layer 1: Source video with reframe crop + effects. Its own stacking context, so the layout's
          z-indexed pane dividers stay under B-roll and the other overlays. */}
      <AbsoluteFill style={{ isolation: "isolate" }}>
        <TrackBoundary name="Video" resetKey={hasScenes ? sceneMetadata : reframeTrack}>
          <EffectsLayer effectsTrack={effectsTrack}>
            {hasScenes ? (
              <SceneReframeLayer sceneMetadata={sceneMetadata!} src={sourceVideoUrl ?? ""} />
            ) : (
              <ReframeLayer reframeTrack={reframeTrack} renderPref={renderPref}>
                <VideoTrack src={sourceVideoUrl} duckSegments={combinedDuckSegments} />
              </ReframeLayer>
            )}
          </EffectsLayer>
        </TrackBoundary>
      </AbsoluteFill>

      {/* Layer 2: B-roll overlays */}
      {renderPref.enableBroll && brollTrack && (
        <TrackBoundary name="B-roll" resetKey={brollTrack}><BRollTrack track={brollTrack} /></TrackBoundary>
      )}

      {/* Layer 3: GIF overlays */}
      {gifTrack && <TrackBoundary name="GIF" resetKey={gifTrack}><GifTrack track={gifTrack} /></TrackBoundary>}

      {/* Layer 4: Emoji overlays */}
      {emojiTrack && <TrackBoundary name="Emoji" resetKey={emojiTrack}><EmojiTrack track={emojiTrack} /></TrackBoundary>}

      {/* Layer 5: Hook text overlays */}
      {renderPref.enableHook && hookTrack && (
        <TrackBoundary name="Text" resetKey={hookTrack}><HookTrack track={hookTrack} sceneMetadata={hasScenes ? sceneMetadata : undefined} avoid={hookAvoid} /></TrackBoundary>
      )}

      {/* Layer 6: Captions */}
      {renderPref.enableCaption && captionTrack && (
        <TrackBoundary name="Captions" resetKey={captionTrack}>
          <CaptionTrack track={captionTrack} sceneMetadata={sceneMetadata} />
        </TrackBoundary>
      )}

      {/* Layer 7: Watermark (topmost visual layer) */}
      <TrackBoundary name="Watermark" resetKey={props.brandWatermark}>
        <WatermarkOverlay visible={applyWatermark} brandWatermark={props.brandWatermark || null} />
      </TrackBoundary>

      {/* Audio layers */}
      {hasScenes && sourceVideoUrl && (
        <TrackBoundary name="Clip audio" resetKey={sourceVideoUrl}>
          <SourceAudio src={sourceVideoUrl} duckSegments={combinedDuckSegments} />
        </TrackBoundary>
      )}
      {renderPref.enableMusic && musicTrack && (
        <TrackBoundary name="Music" resetKey={musicTrack}><MusicTrack track={musicTrack} speechRanges={speechRanges} /></TrackBoundary>
      )}
      {renderPref.enableVoiceover && voiceoverTrack && (
        <TrackBoundary name="Voiceover" resetKey={voiceoverTrack}><VoiceoverTrack track={voiceoverTrack} /></TrackBoundary>
      )}
    </AbsoluteFill>
  )
}

/** Asks the editor for fresh media links (an expired presigned link is the usual reason media fails). */
export const REFRESH_MEDIA_EVENT = "editor:refresh-media"
const requestFreshMedia = () => window.dispatchEvent(new Event(REFRESH_MEDIA_EVENT))

/**
 * In the editor's Player, a track that throws on this edit is replaced by a notice with a Retry button
 * (and logged) instead of blanking the whole preview; it renders again on Retry or once its track
 * changes. The first failure and every Retry ask for fresh media links, which change the track and so
 * retry it. Outside the Player (export) the error is rethrown, so a render never silently drops a layer.
 */
export class TrackBoundary extends React.Component<
  { name: string; resetKey: unknown; children: React.ReactNode },
  { error: unknown; resetKey: unknown }
> {
  state = { error: null as unknown, resetKey: this.props.resetKey }

  static getDerivedStateFromError(error: unknown) {
    return { error }
  }

  static getDerivedStateFromProps(props: { resetKey: unknown }, state: { resetKey: unknown }) {
    return props.resetKey !== state.resetKey ? { error: null, resetKey: props.resetKey } : null
  }

  private askedForFreshMedia = false

  componentDidCatch(error: unknown) {
    if (getRemotionEnvironment().isPlayer) {
      console.warn(`[Remotion] ${this.props.name} track couldn't play and is paused in the preview:`, error)
      if (!this.askedForFreshMedia) requestFreshMedia()
      this.askedForFreshMedia = true
    }
  }

  render() {
    if (!this.state.error) return this.props.children
    if (!getRemotionEnvironment().isPlayer) throw this.state.error
    return (
      <TrackNotice
        name={this.props.name}
        onRetry={() => {
          requestFreshMedia()
          this.setState({ error: null })
        }}
      />
    )
  }
}

/** Says which track stopped, in composition pixels (the Player scales it with the frame). */
const TrackNotice: React.FC<{ name: string; onRetry: () => void }> = ({ name, onRetry }) => (
  <AbsoluteFill style={{ alignItems: "center", justifyContent: "flex-start", paddingTop: "6%", pointerEvents: "none" }}>
    <div
      role="alert"
      style={{
        display: "flex", alignItems: "center", gap: 24, padding: "16px 28px", borderRadius: 999,
        background: "rgba(0,0,0,0.75)", color: "#fff", fontFamily: "sans-serif", fontSize: 34, pointerEvents: "auto",
      }}
    >
      <span>{name} track couldn&apos;t play</span>
      <button
        type="button"
        onClick={onRetry}
        style={{ font: "inherit", fontWeight: 600, color: "#ff6b4a", background: "none", border: "none", cursor: "pointer", padding: 0 }}
      >
        Retry
      </button>
    </div>
  </AbsoluteFill>
)
